/**
 * Minimal Zustand-compatible store factory.
 * Eliminates external zustand dependency while providing the same API:
 * - createStore<T>((set, get) => initialState) → useStore hook
 * - useStore() returns full state, triggers re-render on changes
 * - useStore.getState() / useStore.subscribe() for external access
 */

import { useState, useEffect } from 'react'

export function createStore<T extends object>(
  initializer: (
    set: (partial: Partial<T> | ((state: T) => Partial<T>)) => void,
    get: () => T,
  ) => T,
) {
  let state: T
  const listeners = new Set<() => void>()

  const get = () => state
  const set = (partial: Partial<T> | ((s: T) => Partial<T>)) => {
    const nextPartial = typeof partial === 'function' ? partial(state) : partial
    const nextState = { ...state, ...nextPartial }
    if (!Object.is(state, nextState)) {
      state = nextState
      listeners.forEach((l) => l())
    }
  }
  const subscribe = (listener: () => void) => {
    listeners.add(listener)
    return () => listeners.delete(listener)
  }

  state = initializer(set, get)

  // React hook — universally compatible (React 16+)
  function useStore(): T
  function useStore<U>(selector: (state: T) => U): U
  function useStore<U>(selector?: (state: T) => U): T | U {
    const getSelection = () => (selector ? selector(get()) : get())
    const [selection, setSelection] = useState(getSelection)

    useEffect(() => {
      // Catch edge case where state changed between render and effect
      // CRITICAL: Must wrap in arrow to prevent React from calling function
      // values as state updaters (e.g. when selector returns a function
      // like createAccount, React would call it with prev state as args).
      setSelection(() => getSelection())

      return subscribe(() => {
        const nextSelection = getSelection()
        setSelection((prev) => {
          if (Object.is(prev, nextSelection)) return prev
          return nextSelection
        })
      })
    }, [selector])

    return selection
  }
  useStore.getState = get
  useStore.setState = set
  useStore.subscribe = (cb: (state: T, prevState: T) => void) => {
    let prev = state
    return subscribe(() => {
      const next = state
      cb(next, prev)
      prev = next
    })
  }

  return useStore
}
